import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CustomCardForm } from "@/components/CustomCardForm";
import { SealedProductForm } from "@/components/SealedProductForm";
import { LotFormP6 } from "@/components/publish/LotFormP6";
import { useGame } from "@/lib/game";
import { ReviewSentModal } from "@/components/ReviewSentModal";
import { Package, ArrowRight, RefreshCcw, Layers } from "lucide-react";
import { HitLoadingBlock, HitRing } from "@/components/loading";
import { toast } from "sonner";
import { RequestsP7 } from "@/components/publish/RequestsP7";

const STATUS_LABEL = {
  pending: "En proceso de aprobación",
  approved: "Aprobado — ya puedes cargarlo",
  rejected: "Rechazado",
  EN_REVISION: "En revisión",
  FALTA_DATO: "Falta dato",
  PUBLICADA: "Publicada",
  APROBADA_SIN_PUBLICAR: "Aprobada · sin publicar",
  APROBADA_FALTA_TU_DATO: "Aprobada · falta tu dato",
  RECHAZADA: "Rechazada",
};
const STATUS_PILL = {
  pending: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  approved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  rejected: "bg-red-500/15 text-red-300 border-red-500/40",
  EN_REVISION: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  FALTA_DATO: "bg-[#FF6B5E]/15 text-[#FF6B5E] border-[#FF6B5E]/40",
  PUBLICADA: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  APROBADA_SIN_PUBLICAR: "bg-sky-500/15 text-sky-300 border-sky-500/40",
  APROBADA_FALTA_TU_DATO: "bg-[#FF6B5E]/15 text-[#FF6B5E] border-[#FF6B5E]/40",
  RECHAZADA: "bg-red-500/15 text-red-300 border-red-500/40",
};

/**
 * Apartado "Registrar producto/carta" — le pide al admin agregar algo que
 * NO existe (carta custom o producto sellado). Sub-tabs Carta / Sellado.
 * Debajo del formulario muestra los registros previos del vendedor con su
 * estado (pendiente / aprobado / rechazado) para que sepa cuándo puede
 * pasar a "Cargar" y publicarlo.
 */
export function RegisterItemsPanel({ onNavigateToCargar, onPublishExisting, initialName = "", shippingConfig, onConfigureShip, initialSubTab = "card" }) {
  const nav = useNavigate();
  const [subTab, setSubTab] = useState(initialSubTab); // card | sealed | bundle
  const { activeGame } = useGame();
  const [items, setItems] = useState(null);
  const [reviewSent, setReviewSent] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/my-customs");
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch {
      setItems([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleSuccess = (kind) => {
    setReviewSent({ kind, quantity: 1 });
    load();
  };

  const goCargar = () => {
    if (onNavigateToCargar) onNavigateToCargar();
    else toast.info("Cambia al apartado Cargar producto/carta arriba");
  };

  return (
    <div data-testid="register-panel" className="space-y-6">
      <div className="text-xs text-neutral-500 leading-relaxed bg-[#0A0A0A] border border-[#262626] rounded p-3">
        Registra aquí una carta o producto sellado que <b>no exista aún</b> en el catálogo. El admin lo revisa y aprueba. Cuando esté aprobado, ve al apartado <b>Cargar producto/carta</b> para publicarlo con precio, stock y fotos reales.
      </div>

      {/* Sub-tabs Carta / Sellado / Grupo */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3" data-testid="register-subtabs">
        <button
          type="button"
          data-testid="register-subtab-card"
          onClick={() => setSubTab("card")}
          className={`p-2.5 sm:p-3 rounded-md border-2 text-left transition-colors min-w-0 ${subTab === "card" ? "border-[#EF4444] bg-[#EF4444]/10" : "border-[#262626] bg-[#111] hover:border-neutral-600"}`}
        >
          <div className="font-display text-xs sm:text-sm md:text-base text-white break-words leading-tight">🃏 <span className="hidden sm:inline">Registrar </span>Carta</div>
          <div className="text-[10px] sm:text-[11px] text-neutral-500 mt-0.5 break-words leading-tight">Carta que no está en el catálogo</div>
        </button>
        <button
          type="button"
          data-testid="register-subtab-sealed"
          onClick={() => setSubTab("sealed")}
          className={`p-2.5 sm:p-3 rounded-md border-2 text-left transition-colors min-w-0 ${subTab === "sealed" ? "border-purple-500 bg-purple-500/10" : "border-[#262626] bg-[#111] hover:border-neutral-600"}`}
        >
          <div className="font-display text-xs sm:text-sm md:text-base text-white break-words leading-tight">📦 <span className="hidden sm:inline">Producto </span>Sellado</div>
          <div className="text-[10px] sm:text-[11px] text-neutral-500 mt-0.5 break-words leading-tight">Sobre, caja, ETB, tin…</div>
        </button>
        <button
          type="button"
          data-testid="register-subtab-bundle"
          onClick={() => setSubTab("bundle")}
          className={`p-2.5 sm:p-3 rounded-md border-2 text-left transition-colors min-w-0 ${subTab === "bundle" ? "border-yellow-500 bg-yellow-500/10" : "border-[#262626] bg-[#111] hover:border-neutral-600"}`}
        >
          <div className="font-display text-xs sm:text-sm md:text-base text-white break-words leading-tight">🎴 <span className="hidden sm:inline">Grupo de </span>Cartas</div>
          <div className="text-[10px] sm:text-[11px] text-neutral-500 mt-0.5 break-words leading-tight">Bulk, deck o colección</div>
        </button>
      </div>

      {subTab === "card" && (
        <CustomCardForm key={initialName} initialName={initialName} onSuccess={() => handleSuccess("custom")} onPublishExisting={onPublishExisting} game={activeGame}
          shippingConfig={shippingConfig} onConfigureShip={onConfigureShip} />
      )}
      {subTab === "accessory" && (
        <SealedProductForm accessory onSuccess={() => handleSuccess("sealed")} onCancel={() => setSubTab("card")} game={activeGame} />
      )}
      {subTab === "sealed" && (
        <SealedProductForm onSuccess={() => handleSuccess("sealed")} onCancel={() => setSubTab("card")} game={activeGame} />
      )}
      {subTab === "bundle" && (
        <LotFormP6 game={activeGame} shippingConfig={shippingConfig} onConfigureShip={onConfigureShip}
          onPublished={(p) => { toast.success(p.status === "pending_review" ? "Lote enviado · en revisión por cuenta nueva" : "Lote publicado"); nav("/seller"); }} />
      )}

      {/* Lista de registros del vendedor */}
      <div className="bg-[#111] border border-[#262626] rounded-md p-4 sm:p-6" data-testid="register-my-list">
        <div className="flex items-center justify-between mb-3 gap-2">
          <div className="font-display text-lg">Tus solicitudes</div>
          <Button type="button" size="sm" variant="ghost" onClick={load} disabled={loading} className="text-neutral-400 hover:text-white h-8">
            {loading ? <HitRing tone="light" size={14} /> : <RefreshCcw size={14} />}
          </Button>
        </div>
        {items === null ? (
          <HitLoadingBlock section="inventory" />
        ) : items.length === 0 ? (
          <div className="text-sm text-neutral-500 p-3 bg-[#0A0A0A] border border-[#262626] rounded" data-testid="register-empty">
            Aún no has registrado nada. Cuando registres una carta o un producto sellado, aparecerá aquí con su estado.
          </div>
        ) : (
          <RequestsP7 items={items} reload={load}
            onPublish={(it) => onPublishExisting && onPublishExisting({ id: it.linked_card_id || it.id, name: it.name_es, image_url: it.image_url,
              set_name: it.set_name, local_id: it.number, is_sealed: it.is_sealed, product_type: it.product_type })} />
        )}
        {false && items && (
          <ul className="space-y-2">
            {items.map((it) => {
              const status = it.ui_status || it.custom_status || "pending";
              const isSealed = !!it.is_sealed || String(it.product_type || "").startsWith("sealed_");
              // Resolvemos la miniatura igual que Catalog.jsx: si es URL
              // completa la usamos directa, si es un path del storage la
              // prefijamos con `${BACKEND_URL}/api/files/public/`. Antes
              // usábamos `/api/files/…` (sin `/public/` y sin host), lo que
              // devolvía HTML del SPA en vez de la imagen → aparecían tiles
              // rotos con el icono de imagen faltante.
              const rawImg = it.image_url;
              const idPhoto = it.identification_photos && it.identification_photos[0];
              const imgSrc = rawImg
                ? (String(rawImg).startsWith("http") ? rawImg : String(rawImg).startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${rawImg}` : `${process.env.REACT_APP_BACKEND_URL}/api/files/public/${rawImg}`)
                : (idPhoto ? (String(idPhoto).startsWith("http") ? idPhoto : String(idPhoto).startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${idPhoto}` : `${process.env.REACT_APP_BACKEND_URL}/api/files/public/${idPhoto}`) : null);
              return (
                <li key={it.id} data-testid={`register-item-${it.id}`} className={`flex items-start gap-3 p-3 rounded border ${isSealed ? "border-purple-500/30 bg-purple-500/5" : "border-[#262626] bg-[#0A0A0A]"}`}>
                  <div className={`w-12 h-16 shrink-0 rounded overflow-hidden border ${isSealed ? "border-purple-500/40" : "border-[#262626]"} bg-[#0A0A0A] flex items-center justify-center`}>
                    {imgSrc ? (
                      <img
                        src={imgSrc}
                        alt=""
                        className="w-full h-full object-contain"
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextElementSibling && (e.currentTarget.nextElementSibling.style.display = "flex"); }}
                        data-testid={`register-item-img-${it.id}`}
                      />
                    ) : null}
                    <div style={{ display: imgSrc ? "none" : "flex" }} className="text-[9px] text-neutral-600 text-center px-1 w-full h-full items-center justify-center">Sin foto</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-medium text-white truncate">{it.name_es || it.id}</div>
                      {isSealed && <Package size={12} className="text-purple-400 shrink-0" />}
                    </div>
                    <div className="text-[11px] text-neutral-400 mt-0.5 line-clamp-1">
                      {it.set_name || "—"}{it.serie_name ? ` · ${it.serie_name}` : ""}
                    </div>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border uppercase tracking-wider ${STATUS_PILL[status] || STATUS_PILL.pending}`} data-testid={`register-item-status-${it.id}`}>
                        {STATUS_LABEL[status] || status}
                      </span>
                      {status === "APROBADA_FALTA_TU_DATO" && (
                        <Button type="button" size="sm" onClick={() => nav("/seller?tab=payout")} data-testid={`register-item-fix-${it.id}`} className="h-7 text-[11px] bg-[#FF6B5E] hover:bg-[#ff5446] text-black">
                          Agregar datos de cobro <ArrowRight size={12} className="ml-1" />
                        </Button>
                      )}
                      {(status === "approved" || status === "APROBADA_SIN_PUBLICAR") && (
                        <Button type="button" size="sm" onClick={goCargar} data-testid={`register-item-goto-cargar-${it.id}`} className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700">
                          Ir a Cargar <ArrowRight size={12} className="ml-1" />
                        </Button>
                      )}
                    </div>
                    {status === "APROBADA_FALTA_TU_DATO" && it.auto_publish_seller_issue && (
                      <div className="mt-1 text-[11px] text-[#FF6B5E] bg-[#FF6B5E]/10 border border-[#FF6B5E]/30 rounded px-2 py-1" data-testid={`register-item-issue-${it.id}`}>
                        {it.auto_publish_seller_issue}
                      </div>
                    )}
                    {status === "FALTA_DATO" && it.needs_info && (
                      <div className="mt-1 text-[11px] text-[#FF6B5E] bg-[#FF6B5E]/10 border border-[#FF6B5E]/30 rounded px-2 py-1" data-testid={`register-item-needs-${it.id}`}>
                        {it.needs_info.reason}
                      </div>
                    )}
                    {(status === "rejected" || status === "RECHAZADA") && it.reject_reason && (
                      <div className="mt-1 text-[11px] text-red-300 bg-red-500/10 border border-red-500/30 rounded px-2 py-1">
                        Motivo: {it.reject_reason}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <ReviewSentModal
        open={!!reviewSent}
        onClose={() => setReviewSent(null)}
        kind={reviewSent?.kind || "card"}
        quantity={reviewSent?.quantity || 1}
      />
    </div>
  );
}

export default RegisterItemsPanel;
